import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  MenuItem,
  Tab,
} from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { DraftReallocationAlert } from "features/capital-activity/DraftReallocationAlert";
import { CapitalActivityAdjustmentsModal } from "features/capital-activity/event/adjustments/CapitalActivityAdjustmentsModal";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { CapitalCallDeleteArgs } from "features/capital-activity/event/CapitalEventTypes";
import { useAdjustmentsModal } from "features/capital-activity/event/useAdjustmentsModal";
import { useDraftReallocationState } from "features/capital-activity/event/useHasDraftReallocation";
import { BorrowingBaseSummaryTableBody } from "features/credit-facility/borrowing-base/BorrowingBaseSummaryTableBody";
import {
  getCapitalActivityEventRetrieveQueryKey,
  getCapitalActivityInvestorOverviewRetrieveQueryKey,
  getCapitalActivitySummaryRetrieveQueryKey,
} from "gen/endpoints/capital-activity/capital-activity";
import {
  getCapitalCallAdjustmentsTableRetrieveQueryKey,
  getCapitalCallBreakdownTableDataRetrieveQueryKey,
  getCapitalCallUnfundedBreakdownTableDataRetrieveQueryKey,
  useCapitalCallAdjustmentsTableRetrieveSuspense,
  useCapitalCallBreakdownTableDataRetrieve,
  useCapitalCallDelete,
  useCapitalCallUnfundedBreakdownTableDataRetrieve,
} from "gen/endpoints/capital-call/capital-call";
import { useFundFamilyEventCapitalCallReallocationBreakdownTableRetrieveSuspense } from "gen/endpoints/capital-call-reallocation-breakdown/capital-call-reallocation-breakdown";
import { getCapitalCallReallocationDraftDataRetrieveQueryKey } from "gen/endpoints/capital-call-reallocation-draft-data/capital-call-reallocation-draft-data";
import {
  useCreditFacilityListSuspense,
  useCreditFacilitySummaryRetrieve,
} from "gen/endpoints/credit-facility/credit-facility";
import { getFundFamilyDistributionRetrieveQueryKey } from "gen/endpoints/distributions/distributions";
import {
  getFundFamilyEventRetrieveQueryKey,
  getFundFamilySubEventFirstRetrieveQueryKey,
  getFundFamilySubEventListQueryKey,
  getNoticeDocumentGenerationStatusRetrieveQueryKey,
  getWaterfallRunListQueryKey,
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { AdjustableFundFamilySubEventTypeEnum } from "gen/model/adjustableFundFamilySubEventTypeEnum";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { noop } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetColumnDefFn } from "shared/components/table/data/DataTableTypes";
import { useRefreshOnSubeventsComplete } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ErrorIcon } from "shared/icons/ErrorIcon";
import { WarningIcon } from "shared/icons/WarningIcon";
import { rootRoute } from "shared/navigation/Routes";
import { getErrorMessageFromCeleryWorkflow } from "shared/utils/ErrorUtils";
import { formatMoneyFromDecimal } from "shared/utils/MoneyUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const CapitalCallBreakdownOverviewPage: React.FC<{
  hideEdit?: boolean;
  hideDelete?: boolean;
}> = ({ hideEdit, hideDelete }) => {
  const { fundFamilyId } = useRequiredStringParams();
  // we reuse the same page for the capital call breakdown and cap call reallocation breakdown and so we need to check if the reallocation id is present
  const { fundFamilyEventId: capitalCallId, reallocationFundFamilyEventId } =
    useParams();
  const [isEqualizedView, setIsEqualizedView] = useState(false);
  const fundFamilyEventId = reallocationFundFamilyEventId ?? capitalCallId!;

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const activityEventPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId: capitalCallId! }).$;

  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);
  const creditFacilityId = creditFacilityList[0]?.id;
  const { data: creditFacilitySummaryData } = useCreditFacilitySummaryRetrieve(
    fundFamilyId,
    creditFacilityId,
    {
      fund_family_event_id: fundFamilyEventId,
    },
    {
      query: {
        enabled: !!creditFacilityId,
      },
    },
  );

  const { t } = useTranslation(["common", "capitalCall", "creditFacility"]);

  const { data: tableData, isFetching: isAdjustmentDataFetching } =
    useCapitalCallAdjustmentsTableRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      {
        query: {
          refetchOnMount: true,
        },
      },
    );

  const invalidateQueryKeys = useInvalidateQueryKeys();

  const refetchPageData = useCallback(async () => {
    await invalidateQueryKeys(
      getCapitalCallAdjustmentsTableRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),

      getCapitalCallBreakdownTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),

      getCapitalCallUnfundedBreakdownTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    );
  }, [fundFamilyEventId, fundFamilyId, invalidateQueryKeys]);

  const postSubmit = useCallback(async () => {
    await refetchPageData();
  }, [refetchPageData]);

  const { onSubmit } = useAdjustmentsModal({
    eventData,
    postSubmit,
    subEventTypeBeingAdjusted:
      AdjustableFundFamilySubEventTypeEnum.capital_call,
  });

  const {
    value: isAddAdjustmentsModalOpen,
    setTrue: openAddAdjustmentsModal,
    setFalse: closeAddAdjustmentsModal,
  } = useBoolean(false);

  const {
    outstanding_amount,
    borrowing_base_amount,
    has_borrowing_base_groups,
  } = creditFacilitySummaryData ?? {};
  const borrowingBaseAmount = new Decimal(borrowing_base_amount ?? "0");
  const outstandingAmount = new Decimal(outstanding_amount ?? "0");
  const workflowName = reallocationFundFamilyEventId
    ? t("capitalCall:reallocation.workflow")
    : t("capitalCall:capitalCall");

  const { areAnyProcessing, cache: workflowStatuses } =
    useRefreshOnSubeventsComplete({
      fundFamilyId,
      fundFamilyEventId,
      refetch: refetchPageData,
    });

  const errorMessages = useMemo(
    () =>
      Array.from(workflowStatuses.values())
        .map((status: CeleryWorkflowStatus) =>
          status.state == CeleryWorkflowStateMachineFieldsEnum.failed
            ? getErrorMessageFromCeleryWorkflow(status)
            : undefined,
        )
        .filter((message) => !!message),
    [workflowStatuses],
  );
  const isDraftEvent = eventData.state == "draft";

  const { deleteReallocationEvent } = useDraftReallocationState({
    fundFamilyId,
    fundFamilyEventId: capitalCallId!,
  });

  const navigate = useNavigate();

  const invalidateQueries = useCallback(
    () =>
      invalidateQueryKeys(
        // These are needed for refreshing the main capital activity event page
        getFundFamilyEventRetrieveQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityEventRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
        getWaterfallRunListQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityInvestorOverviewRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getNoticeDocumentGenerationStatusRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilyDistributionRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getCapitalActivitySummaryRetrieveQueryKey(fundFamilyId),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            type: "waterfall",
            raise_if_none: false,
          },
        ),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            raise_if_none: false,
          },
        ),
        getCapitalCallReallocationDraftDataRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
      ),
    [fundFamilyId, fundFamilyEventId, invalidateQueryKeys],
  );

  const onEdit = () =>
    navigate(
      reallocationFundFamilyEventId
        ? rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId: capitalCallId! })
            .calls({})
            .capitalActivityCapitalActivityCallReallocation({
              reallocationFundFamilyEventId,
            }).$
        : rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId })
            .calls({}).$,
    );
  const { mutateAsync: deleteCapitalCall } = useCapitalCallDelete({
    mutation: {
      onSuccess: async () => await invalidateQueries(),
    },
  });
  const { mutateAsync: updateEventToComplete } =
    useFundFamilyEventPendingToCompleteUpdate({
      mutation: {
        onSuccess: async () => await invalidateQueries(),
      },
    });

  const confirmCapitalCallDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<CapitalCallDeleteArgs>,
    {
      title: t("capitalActivity:capitalCallDeleteDialog.title"),
      description: t("capitalActivity:capitalCallDeleteDialog.description"),
      successMessage: t("capitalActivity:capitalCallDeleteDialog.success"),
      performDelete: reallocationFundFamilyEventId
        ? deleteReallocationEvent
        : deleteCapitalCall,
    },
  );

  const onDelete = async () => {
    await confirmCapitalCallDelete({ fundFamilyId, fundFamilyEventId });
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId: capitalCallId! }).$,
    );
  };

  // Only allow finalizing on this screen for reallocations. Normal cap calls get finalized through the larger event being finalized.
  const onFinalize = reallocationFundFamilyEventId
    ? async () => {
        await updateEventToComplete({
          fundFamilyId,
          fundFamilyEventId: reallocationFundFamilyEventId,
        });
        navigate(
          rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId: capitalCallId! }).$,
        );
      }
    : undefined;

  const { hasDraftAllocation } = useDraftReallocationState({
    fundFamilyId,
    fundFamilyEventId: capitalCallId!,
  });

  return (
    <>
      {errorMessages.map((message) => (
        <Alert status="error" key={message} mt="8px">
          <AlertIcon>
            <ErrorIcon />
          </AlertIcon>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      ))}
      <PageHeader
        title={workflowName}
        backButton={{
          title: isDraftEvent
            ? t("common:draftCapitalActivity")
            : t("common:capitalActivity"),
          path: activityEventPath,
        }}
        stats={
          <CapitalEventStatsCard
            capitalActivityData={eventData}
            isEqualizedView={isEqualizedView}
            setIsEqualized={setIsEqualizedView}
            isDraftEvent={isDraftEvent}
            onEdit={hideEdit ? undefined : onEdit}
            onDelete={hideDelete ? undefined : onDelete}
            // Only show reallocation if there is no draft allocation already
            showReallocation={!hasDraftAllocation}
          />
        }
        actions={
          eventData.state === FundFamilyEventStateMachineFieldsEnum.draft ? (
            <Restricted permission={PermissionTypeEnum.write}>
              <PageMenu>
                <MenuItem
                  isDisabled={areAnyProcessing || isAdjustmentDataFetching}
                  onClick={openAddAdjustmentsModal}
                >
                  {t("capitalActivity:adjustments.title")}
                </MenuItem>
              </PageMenu>
            </Restricted>
          ) : undefined
        }
        badge={getCapitalActivityEventStateBadge(eventData.state, t, false)}
      />
      <PageBody>
        <DraftReallocationAlert
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={capitalCallId!}
          reallocationFundFamilyEventId={reallocationFundFamilyEventId}
        />
        <CapitalActivityAdjustmentsModal
          tableData={tableData}
          isLoading={isAdjustmentDataFetching}
          onSubmit={onSubmit}
          isOpen={isAddAdjustmentsModalOpen}
          onClose={closeAddAdjustmentsModal}
        />
        {has_borrowing_base_groups &&
          outstandingAmount.greaterThan(borrowingBaseAmount) && (
            <Alert status="warning" mb="16px">
              <AlertIcon>
                <WarningIcon />
              </AlertIcon>
              <AlertDescription>
                {t(
                  "creditFacility:borrowingBase.outstandingAmountGreaterThanBorrowingBase",
                  {
                    outstandingAmount:
                      formatMoneyFromDecimal(outstandingAmount),
                    borrowingBaseAmount:
                      formatMoneyFromDecimal(borrowingBaseAmount),
                  },
                )}
              </AlertDescription>
            </Alert>
          )}
        <CapitalCallBreakdownTable
          fundFamilyId={eventData.fund_family_id}
          fundFamilyEventId={eventData.id!}
          showBorrowingBaseTable={
            borrowingBaseAmount.greaterThan(0) && !!has_borrowing_base_groups
          }
          creditFacilityId={creditFacilityId}
          isEqualizedView={isEqualizedView}
        />
        {onFinalize && (
          <FormFooter>
            <FormFooterActions>
              <Button colorScheme="primary" onClick={onFinalize}>
                {t("common:finalize")}
              </Button>
            </FormFooterActions>
          </FormFooter>
        )}
      </PageBody>
    </>
  );
};

export const CapitalCallBreakdownFormPage: React.FC = () => {
  const form = useForm();
  const { navigateNext } = usePageWizard();

  const { reallocationFundFamilyEventId } = useParams();

  return (
    <Form form={form} onSubmit={noop} onSuccess={navigateNext}>
      <CapitalCallBreakdownOverviewPage hideEdit={true} hideDelete={true} />
      <PageWizardFormFooter
        submitPermission={
          reallocationFundFamilyEventId
            ? PermissionTypeEnum.approve
            : PermissionTypeEnum.read
        }
      />
    </Form>
  );
};

interface CapitalCallBreakdownTableProps {
  tabColorScheme?: string;
  fundFamilyId: string;
  fundFamilyEventId: string;
  showBorrowingBaseTable: boolean;
  creditFacilityId?: string;
  isEqualizedView: boolean;
}

export const CapitalCallBreakdownTable: React.FC<
  CapitalCallBreakdownTableProps
> = ({
  tabColorScheme,
  fundFamilyId,
  fundFamilyEventId,
  showBorrowingBaseTable,
  creditFacilityId,
  isEqualizedView,
}) => {
  const { data: breakdownTableData, isFetching: isLoadingBreakdown } =
    useCapitalCallBreakdownTableDataRetrieve(
      fundFamilyId,
      fundFamilyEventId,
      {
        is_equalized_view: isEqualizedView,
      },
      {
        query: {
          placeholderData: keepPreviousData,
        },
      },
    );

  const {
    data: unfundedBreakdownTableData,
    isFetching: isLoadingUnfundedBreakdown,
  } = useCapitalCallUnfundedBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const {
    data: reallocationBreakdownTableData,
    isFetching: isLoadingReallocationBreakdown,
  } = useFundFamilyEventCapitalCallReallocationBreakdownTableRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const { t } = useTranslation(["capitalCall", "common"]);
  const [selectedTab, setSelectedTab] = useState(0);

  const tableData = [
    breakdownTableData,
    unfundedBreakdownTableData,
    reallocationBreakdownTableData,
  ];
  const loadingData = [
    isLoadingBreakdown,
    isLoadingUnfundedBreakdown,
    isLoadingReallocationBreakdown,
  ];
  const currentIsLoading = loadingData[selectedTab];
  const currentTableData = tableData[selectedTab];
  const showReallocationTab =
    isEqualizedView && reallocationBreakdownTableData.rows.length > 0;
  return (
    <TableBox isFullWidth={true}>
      <TableBoxHeader>
        <TableBoxTabs
          index={selectedTab}
          onChange={setSelectedTab}
          colorScheme={tabColorScheme}
        >
          <Tab>
            {t("capitalCall:breakdownViewHeaders.capitalCallsBreakdownView")}
          </Tab>
          <Tab>
            {t(
              "capitalCall:breakdownViewHeaders.unfundedCommitmentBreakdownView",
            )}
          </Tab>
          {showReallocationTab && (
            <Tab>{t("capitalCall:breakdownViewHeaders.reallocationView")}</Tab>
          )}
          {showBorrowingBaseTable && (
            <Tab>{t("capitalCall:breakdownViewHeaders.borrowingBase")}</Tab>
          )}
        </TableBoxTabs>
        <DataTableBoxActions />
      </TableBoxHeader>
      {showBorrowingBaseTable &&
      (selectedTab === 3 || (!showReallocationTab && selectedTab == 2)) ? (
        <QueryBoundary>
          <BorrowingBaseSummaryTableBody
            creditFacilityId={creditFacilityId!}
            fundFamilyEventId={fundFamilyEventId}
          />
        </QueryBoundary>
      ) : (
        <TableBoxBody>
          {currentTableData ? (
            <DataTable
              getColumnDef={getColumnDef}
              data={currentTableData}
              isLoading={currentIsLoading}
            />
          ) : (
            <LoadingState />
          )}
        </TableBoxBody>
      )}
    </TableBox>
  );
};

const getColumnDef: GetColumnDefFn = ({ column, rootColumn }) => {
  if (
    rootColumn?.id === "fees" &&
    column.id !== "fees" &&
    (column.title?.length ?? 0) > 10
  ) {
    return {
      width: 220,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  }
};
